import classnames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import { FC, ReactNode, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';

type ResetFormPanelProps = {
  onClose: () => void;
  onResetTabClicked: () => void;
  onRefreshDefaultDatesClicked: () => void;
  onRefreshScheduleClicked: () => void;
};

export const ResetFormPanel: FC<ResetFormPanelProps> = ({
  onClose,
  onResetTabClicked,
  onRefreshDefaultDatesClicked,
  onRefreshScheduleClicked,
}) => {
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);

  const { formatMessage } = useIntl();

  const onResetDateClicked = useCallback(() => {
    onRefreshDefaultDatesClicked();
    onClose();
    logAnalytics.trackEvent(logAnalyticsCategory, 'Refresh dates');
  }, [onRefreshDefaultDatesClicked, logAnalyticsCategory, onClose]);

  const onResetQuoteClicked = useCallback(() => {
    onResetTabClicked();
    onClose();
    logAnalytics.trackEvent(logAnalyticsCategory, 'Reset tab');
  }, [onResetTabClicked, logAnalyticsCategory, onClose]);

  const onResetScheduleClicked = useCallback(() => {
    onRefreshScheduleClicked();
    onClose();
    logAnalytics.trackEvent(logAnalyticsCategory, 'Reset schedule');
  }, [onRefreshScheduleClicked, logAnalyticsCategory, onClose]);

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center p-0 mb-4">
        <FormattedMessage id="form.Reset" defaultMessage="Reset" tagName="h3" />
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} data-e2e="reset-close-btn" />
      </div>
      <div className="card-body p-0">
        <div className="card-text">
          <ResetCard
            icon="restore"
            e2e="reset-dates"
            title={formatMessage({ id: 'form.ResetMainDates', defaultMessage: 'Reset dates to default' })}
            onClick={onResetDateClicked}
          >
            <FormattedMessage
              id="form.ResetMainDates.text"
              defaultMessage="Reset your custom date configurations of this quote to default."
            />
          </ResetCard>
          <ResetCard
            icon="restore"
            e2e="reset-schedule"
            title={formatMessage({ id: 'form.RefreshSchedule', defaultMessage: 'Reset schedule to default' })}
            onClick={onResetScheduleClicked}
          >
            <FormattedMessage
              id="form.RefreshSchedule.text"
              defaultMessage="Reset your custom schedule configurations of this quote to default."
            />
          </ResetCard>
          <ResetCard
            icon="settings_backup_restore"
            e2e="reset-quote"
            title={formatMessage({ id: 'form.ResetQuoteToDefault', defaultMessage: 'Reset quote to default' })}
            className="border-danger text-danger"
            onClick={onResetQuoteClicked}
          >
            <FormattedMessage
              id="form.ResetQuoteToDefault.text"
              defaultMessage="Reset everything of this quote to default. (You will lose all your configurations.)"
            />
          </ResetCard>
        </div>
      </div>
    </div>
  );
};

ResetFormPanel.displayName = 'ResetFormPanel';

interface ResetCardProps {
  children: ReactNode;
  icon: string;
  title: string;
  className?: string;
  onClick?: () => void;
  e2e?: string;
}

const ResetCard: FC<ResetCardProps> = ({ icon, title, className, onClick, e2e, children }) => (
  <div className={classnames('card border mb-2 clickable', className)} onClick={onClick} data-e2e={e2e}>
    <div className="row g-0">
      <div className="col-3 d-flex flex-column justify-content-center align-items-center">
        <i className="icon icon-xl">{icon}</i>
      </div>
      <div className="col-9 ps-0">
        <div className="card-body ps-0 py-3">
          <h5 className="card-title">{title}</h5>
          <p className="card-text">{children}</p>
        </div>
      </div>
    </div>
  </div>
);
